import { apiService } from './apiService';

export const createTenantWithAdmin = async (
  tenantName,
  email,
  password,
  logo,
  role = 'admin',
  firstName,
  lastName,
  attDefaultAddress
) => {
  const body = { tenantName, email, password, role };
  if (logo) body.logo = logo;
  if (firstName != null && String(firstName).trim() !== '') {
    body.firstName = String(firstName).trim();
  }
  if (lastName != null && String(lastName).trim() !== '') {
    body.lastName = String(lastName).trim();
  }
  if (attDefaultAddress && typeof attDefaultAddress === 'object') {
    body.attDefaultAddress = attDefaultAddress;
  }
  return await apiService.request('/super-admin/tenant-admin', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

export const getTenants = async () => {
  return await apiService.request('/tenants', { method: 'GET' });
};

export const getTenantById = async (id) => {
  return await apiService.request(`/tenants/${id}`, { method: 'GET' });
};

export const toggleTenantStatus = async (id, isActive) => {
  return await apiService.request(`/tenants/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ isActive }),
  });
};

/** Preview permanent tenant delete (counts + 30-day eligibility). */
export const getTenantPurgePreview = async (id) => {
  return await apiService.request(`/tenants/${id}/purge-preview`, {
    method: 'GET',
  });
};

/**
 * Permanently delete a hidden tenant after the waiting period.
 * confirmation must match organization code or exact tenant name.
 */
export const permanentlyDeleteTenant = async (
  id,
  { confirmation, acknowledgeDataLoss = true } = {},
) => {
  return await apiService.request(`/tenants/${id}/purge`, {
    method: 'POST',
    body: JSON.stringify({ confirmation, acknowledgeDataLoss }),
  });
};

export const updateTenant = async (id, body) => {
  return await apiService.request(`/tenants/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};

export const updateTenantLogo = async (tenantId, logo) => {
  const res = await getTenantById(tenantId);
  if (!res.success || !res.data?.tenant) {
    throw new Error(res.message || 'Failed to load tenant');
  }
  const current = res.data.tenant;
  const settings = current.settings || {};
  const branding = settings.branding || {};
  const updatedSettings = {
    ...settings,
    branding: { ...branding, logo },
  };
  return await updateTenant(tenantId, { settings: updatedSettings });
};

/**
 * Persists default import address on the tenant when a row has no street (merged with current settings).
 */
export const updateTenantAttDefaultAddress = async (tenantId, attDefaultAddress) => {
  const res = await getTenantById(tenantId);
  if (!res.success || !res.data?.tenant) {
    return {
      success: false,
      message: res.message || 'Failed to load tenant',
    };
  }
  const current = res.data.tenant;
  const settings = {
    ...(current.settings || {}),
    attDefaultAddress: {
      street_address: String(attDefaultAddress.street_address ?? '').trim(),
      city: String(attDefaultAddress.city ?? '').trim(),
      state: String(attDefaultAddress.state ?? '').trim(),
      zip: String(attDefaultAddress.zip ?? '').trim(),
      country: String(attDefaultAddress.country ?? '').trim(),
    },
  };
  return await updateTenant(tenantId, { settings });
};

/**
 * Persists the fallback billing account used only for Nitel inventory imports.
 */
export const updateTenantNitelBillingAccount = async (tenantId, nitelBillingAccount) => {
  const res = await getTenantById(tenantId);
  if (!res.success || !res.data?.tenant) {
    return {
      success: false,
      message: res.message || 'Failed to load tenant',
    };
  }
  const current = res.data.tenant;
  const settings = {
    ...(current.settings || {}),
    nitelBillingAccount: String(nitelBillingAccount ?? '').trim(),
  };
  return await updateTenant(tenantId, { settings });
};

/** Default site classification labels when a tenant has not customized them. */
export const DEFAULT_SITE_CLASSIFICATIONS = [
  'Data Center',
  'HQ',
  'ASR',
  'Tier 1',
  'Tier 2',
  'Tier 3',
  'Tier 4',
  'Other',
];

/**
 * Persist tenant-configurable Site Classification dropdown values
 * (merged into tenants.settings.siteClassifications).
 */
export const updateTenantSiteClassifications = async (tenantId, siteClassifications) => {
  const res = await getTenantById(tenantId);
  if (!res.success || !res.data?.tenant) {
    return {
      success: false,
      message: res.message || 'Failed to load tenant',
    };
  }
  const seen = new Set();
  const cleaned = [];
  for (const item of Array.isArray(siteClassifications) ? siteClassifications : []) {
    const name = String(item ?? '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    cleaned.push(name);
  }
  if (cleaned.length === 0) {
    return {
      success: false,
      message: 'Add at least one site classification name.',
    };
  }
  const current = res.data.tenant;
  const settings = {
    ...(current.settings || {}),
    siteClassifications: cleaned,
  };
  return await updateTenant(tenantId, { settings });
};

export const generatePowerBIKey = async (tenantId) => {
  return await apiService.request(`/super-admin/tenants/${tenantId}/powerbi-key`, {
    method: 'POST',
  });
};

export const getTenantPowerBIKey = async (tenantId) => {
  return await apiService.request(`/super-admin/tenants/${tenantId}/powerbi-key`, {
    method: 'GET',
  });
};

export const regeneratePowerBIKey = async (tenantId) => {
  return await apiService.request(`/super-admin/tenants/${tenantId}/powerbi-key`, {
    method: 'PUT',
  });
};

export const getSuperAdminSettings = async () => {
  return await apiService.request('/super-admin/settings', { method: 'GET' });
};

export const getClaudeModels = async () => {
  return await apiService.request('/super-admin/claude-models', { method: 'GET' });
};

export const getTenantBillingAccounts = async (tenantId) => {
  return await apiService.request(
    `/super-admin/tenants/${tenantId}/billing-accounts`,
    { method: 'GET' },
  );
};

export const updateTenantBillingAccounts = async (tenantId, data) => {
  return await apiService.request(
    `/super-admin/tenants/${tenantId}/billing-accounts`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
};

export const getTenantGlCodes = async (tenantId) => {
  return await apiService.request(
    `/super-admin/tenants/${tenantId}/gl-codes`,
    { method: 'GET' },
  );
};

export const updateTenantGlCodes = async (tenantId, data) => {
  return await apiService.request(
    `/super-admin/tenants/${tenantId}/gl-codes`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    },
  );
};

export const updateSuperAdminSettings = async (settings) => {
  return await apiService.request('/super-admin/settings', {
    method: 'PUT',
    body: JSON.stringify(settings),
  });
};

export const updateTenantInvoiceEmailSettings = async (tenantId, emailSettings) => {
  return await updateTenant(tenantId, {
    emailDisplayName: emailSettings.emailDisplayName,
    emailFrom: emailSettings.emailFrom,
    emailTo: emailSettings.emailTo,
    emailCc: emailSettings.emailCc,
    emailSubject: emailSettings.emailSubject,
    customField1Name: emailSettings.customField1Name,
    customField2Name: emailSettings.customField2Name,
    invoiceNoteAutoEnabled: emailSettings.invoiceNoteAutoEnabled,
    invoiceMrcChangeThreshold: emailSettings.invoiceMrcChangeThreshold,
    invoiceClaudePrompt: emailSettings.invoiceClaudePrompt,
    monthSummaryClaudePrompt: emailSettings.monthSummaryClaudePrompt,
    monthSummaryAutoOnClose: emailSettings.monthSummaryAutoOnClose,
    invoiceGrammarClaudePrompt: emailSettings.invoiceGrammarClaudePrompt,
    monthSummaryGrammarClaudePrompt:
      emailSettings.monthSummaryGrammarClaudePrompt,
    invoiceClaudeModel: emailSettings.invoiceClaudeModel,
    invoiceGrammarClaudeModel: emailSettings.invoiceGrammarClaudeModel,
    monthSummaryClaudeModel: emailSettings.monthSummaryClaudeModel,
    monthSummaryGrammarClaudeModel:
      emailSettings.monthSummaryGrammarClaudeModel,
    invoiceExtractionClaudePrompt: emailSettings.invoiceExtractionClaudePrompt,
    invoiceExtractionClaudeModel: emailSettings.invoiceExtractionClaudeModel,
  });
};
